/**
 * uiSlice — global UI/layout preferences.
 *
 * Holds cross-route UI state that must survive route transitions:
 * sidebar collapsed state, theme preference, active panel/drawer,
 * and table row density.
 *
 * RULES:
 * - No server entities here; server state lives in TanStack Query.
 * - No PII. Display names, emails, phone numbers must not be stored here.
 */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export type ThemePreference = 'light' | 'dark' | 'system';
export type RowDensity = 'compact' | 'default' | 'comfortable';

export interface UiState {
  /** Whether the main left-hand sidebar is collapsed. */
  sidebarCollapsed: boolean;
  /** User-chosen color scheme preference. */
  theme: ThemePreference;
  /** The ID of the currently active panel (e.g. call detail side panel). Null when none open. */
  activePanelId: string | null;
  /** The ID of the currently active drawer. Null when none open. */
  activeDrawerId: string | null;
  /** Default row density for data-dense tables. */
  density: RowDensity;
}

const initialState: UiState = {
  sidebarCollapsed: false,
  theme: 'system',
  activePanelId: null,
  activeDrawerId: null,
  density: 'default',
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    setTheme(state, action: PayloadAction<ThemePreference>) {
      state.theme = action.payload;
    },
    openPanel(state, action: PayloadAction<string>) {
      state.activePanelId = action.payload;
    },
    closePanel(state) {
      state.activePanelId = null;
    },
    openDrawer(state, action: PayloadAction<string>) {
      state.activeDrawerId = action.payload;
    },
    closeDrawer(state) {
      state.activeDrawerId = null;
    },
    setDensity(state, action: PayloadAction<RowDensity>) {
      state.density = action.payload;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarCollapsed,
  setTheme,
  openPanel,
  closePanel,
  openDrawer,
  closeDrawer,
  setDensity,
} = uiSlice.actions;

// Selectors
export const selectSidebarCollapsed = (state: { ui: UiState }) => state.ui.sidebarCollapsed;
export const selectTheme = (state: { ui: UiState }) => state.ui.theme;
export const selectActivePanelId = (state: { ui: UiState }) => state.ui.activePanelId;
export const selectActiveDrawerId = (state: { ui: UiState }) => state.ui.activeDrawerId;
export const selectDensity = (state: { ui: UiState }) => state.ui.density;
